/*
 * Created Date: Sunday April 3rd 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { Button, Grid, Stack } from "@mui/material";
import ToolTip from "./ToolTip";
import { ReactNode, useMemo } from "react";
import { InfoOutlined } from "@mui/icons-material";

export interface CustomButtonProps {
  align?: string;
  color?: "green" | "blue" | "grey" | "red";
  variant?: "contained" | "outlined" | "text";
  disabled?: boolean;
  endIcon?: any;
  fullWidth?: boolean;
  id?: string;
  labelColor?: string;
  onClick?: any;
  startIcon?: any;
  style?: any;
  sx?: any;
  text?: string;
  selected?: boolean;
  infoContent?: ReactNode;
  children?: ReactNode;
}

const CustomButton = ({
  align,
  color,
  variant,
  disabled,
  endIcon,
  fullWidth,
  id,
  labelColor,
  startIcon,
  text,
  selected,
  infoContent,
  children,
  sx,
  ...other
}: CustomButtonProps) => {
  const themeColor = useMemo(() => {
    if (variant === "outlined") {
      return "info";
    } else if (color === "blue") {
      return "primary";
    } else if (color === "green") {
      return "success";
    } else if (color === "red") {
      return "error";
    } else {
      return "secondary";
    }
  }, [color, variant]);

  return (
    <ToolTip content={infoContent}>
      {/* Extra stack because of posibility of button being disabled */}
      <Stack width={fullWidth ? "100%" : undefined}>
        <Button
          id={id}
          fullWidth={fullWidth}
          disabled={disabled}
          endIcon={endIcon}
          startIcon={startIcon}
          color={themeColor}
          variant={variant || "contained"}
          sx={{
            color: variant === "text" ? "text.primary" : null,
            backgroundColor: selected ? "#29405B" : null,
            "&:hover": {
              backgroundColor: selected
                ? "#273B54"
                : variant === "text"
                ? "action.hover"
                : null,
            },
            "&.Mui-disabled": {
              backgroundColor: themeColor + ".main",
            },
            display: "grid",
            gridTemplateColumns: "auto 1fr auto auto",
            userSelect: "none",
            minWidth: variant === "text" ? "max-content" : "64px",
            padding: variant === "text" ? "6px 12px " : null,
            "& .MuiButton-startIcon": {
              gridColumn: 1,
            },
            "& .MuiButton-endIcon": {
              gridColumn: 4,
              marginRight: "-5px",
            },
            ...sx,
          }}
          {...other}
        >
          <Grid
            item
            gridColumn={2}
            textAlign={align === "left" ? "left" : "center"}
          >
            {children}
            {text}
          </Grid>
          {infoContent && (
            <Grid
              item
              gridColumn={endIcon ? 3 : 4}
              display={"flex"}
              alignContent={"center"}
            >
              <InfoOutlined
                sx={{
                  marginRight: !endIcon ? "-5px" : undefined,
                  cursor: "pointer",
                  marginLeft: "12px",
                  width: "20px",
                  height: "20px",
                }}
              />
            </Grid>
          )}
          {labelColor && (
            <div
              style={{
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
                position: "absolute",
                width: 8,
                height: "100%",
                left: 0,
                backgroundColor: labelColor,
                zIndex: 1,
              }}
            />
          )}
        </Button>
      </Stack>
    </ToolTip>
  );
};

export default CustomButton;
